/**
 * /operations/attendance/wfh-register — Session 3B PR 6.
 *
 * Owning agent: Tashghil. Who's working from home + geofence check.
 */

import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, MapPin, Plus, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { WfhRegisterItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

function _statusToneClass(s: WfhRegisterItem["wfh_status"]): string {
  if (s === "approved") return "bg-emerald-100 text-emerald-800";
  if (s === "denied") return "bg-rose-100 text-rose-800";
  return "bg-amber-100 text-amber-800";
}

export function WfhRegisterPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [dateFilter, setDateFilter] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const list = useQuery({
    queryKey: ["session3b-wfh-register", legalEntityId, dateFilter],
    queryFn: () => session3bApi.listWfhRegister(legalEntityId ?? "", dateFilter),
    enabled: !!legalEntityId,
  });

  const items: WfhRegisterItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const unverified = items.filter((r) => r.geofence_match === false).length;
    return {
      total: items.length,
      attention: unverified,
      critical: 0,
      approved: items.filter((r) => r.wfh_status === "approved").length,
      pending: items.filter((r) => r.wfh_status === "pending").length,
      first_unverified: items.find((r) => r.geofence_match === false),
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("attendance.wfh_register.title", "WFH Register")}
          subtitle={t(
            "attendance.wfh_register.subtitle",
            "Who's working from home today. Tashghil tracks geofence verification when geolocation is enabled.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-text-secondary">
              {t("attendance.wfh_register.date_label", "Date")}
            </label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="rounded-lg border border-border-subtle px-2 py-1 text-xs"
            />
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "attendance.wfh_register.cta.record_note",
                  "Record WFH on behalf — drawer with employee + date + reason picker; writes a wfh_request row.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("attendance.wfh_register.cta.record", "Record WFH Day on behalf")}
          </button>
        </div>

        {counts.first_unverified && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<MapPin className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "attendance.wfh_register.suggestion.unverified_headline",
              `${counts.attention} WFH day${counts.attention === 1 ? "" : "s"} have no geofence match.`,
            )}
            body={t(
              "attendance.wfh_register.suggestion.unverified_body",
              "Tashghil notes: the employee likely clocked in from a different location, or geolocation was off. Review before approval.",
            )}
            primaryAction={{
              label: t("attendance.wfh_register.suggestion.view_employee", "View employee"),
              onClick: () =>
                window.location.assign(`/people/employees/${counts.first_unverified!.employee_id}`),
            }}
            suggestionKey={`wfh-unverified-${counts.first_unverified.employee_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "attendance.wfh_register.suggestion.empty_headline",
              "No WFH records for this date.",
            )}
            body={t(
              "attendance.wfh_register.suggestion.empty_body",
              "Tashghil notes: nobody's logged a WFH day. If that's a surprise, check attendance for clock-ins from elsewhere.",
            )}
            primaryAction={{
              label: t("attendance.wfh_register.suggestion.open_grid", "View attendance grid"),
              onClick: () => window.location.assign("/operations/attendance"),
            }}
            suggestionKey="wfh-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("attendance.wfh_register.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("attendance.wfh_register.col.date", "Date")}</th>
                  <th className="px-3 py-2">{t("attendance.wfh_register.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("attendance.wfh_register.col.geofence", "Geofence")}</th>
                  <th className="px-3 py-2">{t("attendance.wfh_register.col.reason", "Reason")}</th>
                  <th className="px-3 py-2">{t("attendance.wfh_register.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.record_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <span dir="ltr">{row.date}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.wfh_status)}`}
                      >
                        {row.wfh_status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "attendance.wfh_register.trace.geofence",
                              `geofence_match = true when clock-in geolocation falls inside the home polygon registered for this employee. null = geolocation was not captured (older record or disabled at the device). false = clock-in came from a different location (review).`,
                            ),
                          )
                        }
                      >
                        <span className="text-xs text-text-secondary">
                          {row.geofence_match === null
                            ? "—"
                            : row.geofence_match
                              ? t("attendance.wfh_register.geofence.match", "match")
                              : t("attendance.wfh_register.geofence.mismatch", "mismatch")}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-tertiary">{row.reason}</td>
                    <td className="px-3 py-2">
                      {row.wfh_status === "pending" && (
                        <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "attendance.wfh_register.action.approve_note",
                                  "Approve WFH — wires to the WFH workflow advance endpoint.",
                                ),
                              )
                            }
                            className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                          >
                            <CheckCircle2 className="me-1 inline h-3 w-3" />
                            {t("attendance.wfh_register.action.approve", "Approve")}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "attendance.wfh_register.action.deny_note",
                                  "Deny WFH — prompts for reason then logs workflow_event.",
                                ),
                              )
                            }
                            className="rounded border border-rose-300 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 hover:bg-rose-50"
                          >
                            <XCircle className="me-1 inline h-3 w-3" />
                            {t("attendance.wfh_register.action.deny", "Deny")}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("attendance.wfh_register.title", "WFH Register"),
            window_label: `${counts.approved} approved · ${counts.pending} pending`,
          }}
        />
      </aside>
    </div>
  );
}
